import { Box, Button, Card, Drawer, Group, Text } from '@mantine/core';
import { IconChevronDown, IconCopy, IconTrash } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { PageTitle } from 'components/PageTitle';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory, useLocation, useRouteMatch } from 'react-router';
import { isNonNullish } from 'remeda';
import MoveRecoveryPlanInvoices from 'shared/action-component/MoveRecoveryPlanInvoices/MoveRecoveryPlanInvoices';
import AYSModal from 'shared/components/AYSModal';
import NavigationPrompt from 'shared/components/BlockNavigation';
import { RecoveryPlan } from 'shared/components/RecoveryPlan';
import { useIsMobile } from 'shared/hooks/utils/use-is-mobile';
import { ButtonColor, Dropdown, Input } from 'shared/io';
import { recoveryPlanSerializer, treatRecoveryPlan } from 'shared/serializer';
import { usePrevious } from 'shared/utils/hooks';
import { settingsActions } from 'store/settings/settings.actions';
import { DialogShowId, DialogShowSize, dialogHide, showDialog } from 'store/view/view.actions';
import type {
  RecoverActionType,
  RecoveryPlanStep,
  RecoveryPlan as RecoveryPlanT,
} from 'types/recovery-plan';
import { RECOVERY_PLANS_QUERY_KEY } from '../api';
import styleIdentifiers from './EditDebtCollectionCustomization.module.scss';
import RecoveryStepForm from './RecoveryStepForm';
import { openRecoveryTaskStepModal } from './RecoveryStepForm/openRecoveryTaskStepModal';

const styles = classNames.bind(styleIdentifiers);

type RecoveryPlanType = RecoveryPlanT & {
  step_deleted?: boolean;
  isDirty: boolean;
};

export default function EditDebtCollectionCustomization() {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();
  const history = useHistory();
  const queryClient = useQueryClient();
  const location = useLocation();
  const match = useRouteMatch();
  const isMobile = useIsMobile();
  const [dbc, setDbc] = useState<RecoveryPlanType>();
  const [stepForm, setStepForm] = useState<StepForm | null>(null);

  const closeStepForm = () => setStepForm(null);
  const [recoveryPlan, setRecoveryPlan] = useState<RecoveryPlanType>();
  const [feeStartingStep, setFeeStartingStep] = useState<number | null>(null);
  const form = useForm({ shouldUnregister: true });
  const {
    register,
    reset,
    trigger,
    getValues,
    formState: { errors },
  } = form;
  const { params } = useRouteMatch();
  const previousStepLength = usePrevious(dbc?.recovery_steps);

  useEffect(() => {
    if (dbc) {
      const newDbc = { ...dbc } as any;

      newDbc.recovery_steps = dbc!.recovery_steps.slice();
      let shouldAddFees = false;
      newDbc.recovery_steps = newDbc.recovery_steps.map((step: any) => {
        if (feeStartingStep === step.order) {
          shouldAddFees = true;
        }
        return {
          ...step,
          fee_starting_step: feeStartingStep === step.order,
          fee_step_reached: shouldAddFees,
        };
      });
      if (previousStepLength !== undefined) {
        newDbc.isDirty = true;
      }
      setDbc(newDbc);
    }
  }, [feeStartingStep, dbc?.recovery_steps.length]);

  useEffect(() => {
    if (match.path.indexOf('create') !== -1) {
      setDbc({
        id: undefined as any,
        recovery_steps: [],
        name: '',
        isDirty: true,
        invoices_using: 0,
        debtors_using: 0,
      });
    } else {
      settingsActions.getRecoveryPlan({
        id: (params as any).id,
        callback: ({ data }: any) => {
          const recoveryPlan = treatRecoveryPlan(data) as RecoveryPlanType;
          reset({ name: recoveryPlan.name });
          setFeeStartingStep(
            recoveryPlan.recovery_steps.find(step => step.fee_step_reached)?.order || null,
          );
          setDbc(recoveryPlan);
          setRecoveryPlan(recoveryPlan);
        },
      });
    }
  }, [location]);

  const editStep = (stepIndex: any) => () => {
    const index = stepIndex === -1 ? 0 : stepIndex;
    const step = dbc!.recovery_steps[index];

    const applyEditedStep = (edited: RecoveryPlanStep) => {
      const newDbc = { ...dbc!, isDirty: true };
      newDbc.recovery_steps[index] = { ...edited, order: index };
      setDbc(newDbc);
    };

    if (step.step_type === 'task_generation' && company.package.can_use_tasks) {
      openRecoveryTaskStepModal({
        title: t(i18nKeys.EDIT_TASK),
        step,
        onSubmit: applyEditedStep,
      });
      return;
    }

    setStepForm({
      step,
      availableTemplateTypes: [step.step_type],
      onSelectTemplate: edited => {
        applyEditedStep(edited);
        closeStepForm();
      },
    });
  };

  const addStep = (index: any) => (_onlyPreventiveReminder: boolean, before_due_date: boolean) => {
    const preventiveReminder = dbc!.recovery_steps.find(e => e.step_type === 'preventive_reminder');

    const firstReminder = dbc!.recovery_steps.find(e => e.step_type === 'first_reminder');

    const lastReminder = dbc!.recovery_steps.find(e => e.step_type === 'last_reminder');

    let availableTemplateTypes: RecoverActionType[] = [];

    if (before_due_date && !preventiveReminder) {
      availableTemplateTypes = ['preventive_reminder'];
    } else if (!before_due_date && !firstReminder) {
      availableTemplateTypes = ['first_reminder'];
    } else if (
      !before_due_date &&
      firstReminder &&
      !lastReminder &&
      index > dbc!.recovery_steps.indexOf(firstReminder)
    ) {
      availableTemplateTypes = ['intermediate_reminder', 'last_reminder'];
    } else if (
      !before_due_date &&
      firstReminder &&
      lastReminder &&
      index > dbc!.recovery_steps.indexOf(firstReminder) &&
      index <= dbc!.recovery_steps.indexOf(lastReminder)
    ) {
      availableTemplateTypes = ['intermediate_reminder'];
    }

    const insertStep = (step: RecoveryPlanStep) => {
      step.before_due_date = before_due_date;
      const newDbc = { ...dbc!, isDirty: true };
      const recoverySteps = newDbc.recovery_steps.slice();
      recoverySteps.splice(index, 0, step);
      let shouldAddFees = false;
      let newOrder: any = null;
      newDbc.recovery_steps = recoverySteps.map((step, index) => {
        if (step.fee_starting_step) {
          shouldAddFees = true;
          newOrder = index;
        }
        return {
          ...step,
          fee_starting_step:
            step.fee_starting_step && step.fee_starting_step === step.fee_step_reached,
          fee_step_reached: shouldAddFees,
          order: index,
        };
      });

      setDbc(newDbc);
      setFeeStartingStep(newOrder);
    };

    if (!availableTemplateTypes.length && company.package.can_use_tasks) {
      openRecoveryTaskStepModal({
        title: t(i18nKeys.ADD_TASK),
        onSubmit: insertStep,
      });
      return;
    }

    setStepForm({
      availableTemplateTypes,
      onlyTask: !availableTemplateTypes.length,
      onSelectTemplate: step => {
        insertStep(step);
        closeStepForm();
      },
    });
  };

  const refreshPlanList = () =>
    queryClient.invalidateQueries({ queryKey: RECOVERY_PLANS_QUERY_KEY });

  const updatePlan = (callback: any, steps_switch?: any) => {
    settingsActions[dbc!.id ? 'setRecoveryPlan' : 'addRecoveryPlan']({
      id: dbc!.id,
      data: recoveryPlanSerializer(
        { ...dbc, name: getValues().name, steps_switch },
        (dbc!.id && recoveryPlan) || undefined,
      ),
      callback: ({ data }: any) => {
        refreshPlanList();
        dialogHide(DialogShowId.CUSTOM);
        if (callback) {
          callback();
        } else if (dbc!.id) {
          const plan = treatRecoveryPlan(data);
          setFeeStartingStep(
            plan.recovery_steps.find((step: any) => step.fee_step_reached)?.order || null,
          );
          setRecoveryPlan(plan);
          setDbc({ ...plan, isDirty: false });
        } else {
          setDbc({ ...dbc, isDirty: false } as any);
          setTimeout(() => {
            history.push(`/settings/workflows/recovery-plans/edit/${data.id}`);
          }, 50);
        }
      },
    });
  };

  const save = (isSaveAndQuit: boolean) => async (callback: any) => {
    const isValid = await trigger();

    if (!isValid) return;

    if (recoveryPlan && recoveryPlan!.invoices_using > 0 && dbc!.step_deleted) {
      MoveRecoveryPlanInvoices({
        title: t(i18nKeys.PLAN.INVOICES),
        newPlan: dbc,
        oldPlan: recoveryPlan,
        allStepRequired: true,
        onSubmit: (steps_switch: any) => {
          updatePlan(isSaveAndQuit && callback, steps_switch);
        },
      });
    } else {
      updatePlan(isSaveAndQuit && callback);
    }
  };

  const deletePlan = () => {
    setDbc({
      ...dbc,
      isDirty: false,
    } as any);
    showDialog({
      id: DialogShowId.CONFIRM,
      size: DialogShowSize.SMALL,
      keepMountOnExit: true,
      title: t(i18nKeys.CONFIRM),
      children: (
        <AYSModal
          text={t(i18nKeys.AYS.DELETE_RECOVERY_PLAN)}
          confirmButtonColor={ButtonColor.RED}
          confirmButtonText={t(i18nKeys.DELETE)}
          onConfirm={() =>
            settingsActions.deleteRecoveryPlan({
              id: dbc!.id,
              callback: () => {
                refreshPlanList();
                history.push('/settings/workflows/recovery-plans');
              },
            })
          }
        />
      ),
    });
  };

  const deleteStep = (index: number) => () => {
    const newDbc = { ...dbc!, isDirty: true };
    newDbc.recovery_steps = newDbc.recovery_steps!.slice();
    if (newDbc.recovery_steps[index].fee_starting_step) {
      if (newDbc.recovery_steps[index + 1]) {
        newDbc.recovery_steps[index + 1] = {
          ...newDbc.recovery_steps[index + 1],
          fee_starting_step: true,
          fee_step_reached: true,
        };
        setFeeStartingStep(newDbc.recovery_steps[index + 1].order);
      } else {
        setFeeStartingStep(null);
      }
    }
    if (newDbc.recovery_steps[index].invoices_count > 0) {
      newDbc.step_deleted = true;
    }
    newDbc.recovery_steps.splice(index, 1);

    setDbc(newDbc);
  };

  const duplicate = () => {
    settingsActions.duplicateRecoveryPlan({
      id: dbc!.id,
      callback: ({ data }: any) => {
        refreshPlanList();
        history.push(`/settings/workflows/recovery-plans/edit/${data.id}`);
      },
    });
  };
  const setDirty = () => {
    setDbc({ ...dbc, isDirty: true } as any);
  };

  if (!dbc) return null;

  return (
    <div className={styles('edit-debt-collection-customization')}>
      <PageTitle>
        <PageTitle.Back />
        {t(i18nKeys.AUTOMATED_PLANS)}
        <PageTitle.Actions>
          {isMobile && (
            <Dropdown
              className={styles('actions-dropdown')}
              toggleContent
              selectorContent={
                <Button rightSection={<IconChevronDown size={18} />}>
                  {t(i18nKeys.FORM.ACTIONS)}
                </Button>
              }
            >
              {recoveryPlan?.id && (
                <>
                  <div className={styles('dropdown-item')} onClick={deletePlan}>
                    {t(i18nKeys.DELETE)}
                  </div>
                  <div className={styles('dropdown-item')} onClick={duplicate}>
                    {t(i18nKeys.DUPLICATE)}
                  </div>
                </>
              )}
              <div className={styles('dropdown-item')} onClick={save(false)}>
                {t(i18nKeys.SAVE)}
              </div>
            </Dropdown>
          )}
          {!isMobile && (
            <>
              {recoveryPlan?.id && (
                <>
                  <Button
                    onClick={deletePlan}
                    color="error.5"
                    variant="outline"
                    aria-label={t(i18nKeys.DELETE)}
                  >
                    <IconTrash size={18} />
                  </Button>
                  <Button
                    leftSection={<IconCopy size={18} />}
                    onClick={duplicate}
                    variant="outline"
                  >
                    {t(i18nKeys.DUPLICATE)}
                  </Button>
                </>
              )}
              <Button onClick={save(false)}>{t(i18nKeys.SAVE)}</Button>
            </>
          )}
        </PageTitle.Actions>
      </PageTitle>
      <NavigationPrompt when={dbc.isDirty} onSaveAndQuit={save(true)} />
      <Card radius={16}>
        <Group justify="space-between">
          <Group>
            <Input
              register={register('name', { required: true })}
              style={{ width: 400 }}
              placeholder={t(i18nKeys.PLAN.NAME)}
              errorMessage={errors.name}
              onValueChanged={setDirty}
              noMargin
            />
          </Group>
          <Box>
            {recoveryPlan && (
              <Text>
                {t(i18nKeys.FORM.REFERENCE)} : {recoveryPlan.id}
              </Text>
            )}
          </Box>
        </Group>

        <FormProvider {...form}>
          {!company.package.can_edit_plan ? (
            <RecoveryPlan
              onClickOnStep={editStep}
              setLateFees={setFeeStartingStep}
              recoverPlan={dbc}
              editStep={editStep}
              lateFeesStep={feeStartingStep}
            />
          ) : (
            <RecoveryPlan
              onClickOnStep={editStep}
              setLateFees={setFeeStartingStep}
              recoverPlan={dbc}
              addStep={addStep}
              editStep={editStep}
              deleteStep={deleteStep}
              lateFeesStep={feeStartingStep}
              editMode
            />
          )}
        </FormProvider>
      </Card>
      <Drawer position="right" opened={isNonNullish(stepForm)} onClose={closeStepForm}>
        {isNonNullish(stepForm) && (
          <RecoveryStepForm
            step={stepForm.step}
            availableTemplateTypes={stepForm.availableTemplateTypes}
            onlyTask={stepForm.onlyTask}
            onSelectTemplate={stepForm.onSelectTemplate}
            onClose={closeStepForm}
          />
        )}
      </Drawer>
    </div>
  );
}

type StepForm = {
  availableTemplateTypes: RecoverActionType[];
  onSelectTemplate: (step: RecoveryPlanStep) => void;
  step?: RecoveryPlanStep;
  onlyTask?: boolean;
};
